import { bindable } from 'aurelia-templating';
import { autoinject, transient } from 'aurelia-dependency-injection';
import { BindingSignaler } from "aurelia-templating-resources";
import { type StepModel } from '../../../models/basics/basics.models';
import { computedFrom } from 'aurelia-binding';
import { Reflection } from '../../../utilities/reflection';
import { Router } from 'aurelia-router';
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { Messages } from '../../../configuration/event-messages.constants';

@autoinject()
@transient()
export class EntityItem {

    private _subscriptions: Subscription[] = [];
    private _currentStep: StepModel;

    @bindable
    public cacheTabKey: string = null;

    @bindable
    public steps: StepModel[] = [];

    @bindable
    public defaultStepResolver: (steps: StepModel[]) => StepModel = null;

    @bindable
    public shouldRemoveCacheOnDetached: boolean = true;

    @bindable
    public item: any = {};

    @bindable
    public onBeforeStepChanged: (currentStep: StepModel, nextStep: StepModel) => void | Promise<void>;

    @bindable
    public onStepClicked: any;

    @computedFrom('steps')
    get noOfStepsShowing() {
        const arrays = this.steps.filter(step => typeof (step.show) == "function" ? step.show() == true : step.show == true);
        return arrays.length;
    }

    @computedFrom(Reflection.getPropertyPath<EntityItem>(x => x._currentStep))
    get currentStep(): StepModel {
        return this._currentStep;
    }

    set currentStep(value) {
        const callback = () => {
            this._currentStep = value;

            if (this.steps && this.steps.length > 0) {
                this.itemChanged();
            }

            if (this.onStepClicked) {
                this.onStepClicked(value);
            }

            if (this._currentStep) {
                this.bindingSignaler.signal('refresh-show-step-' + this._currentStep.step);
            }
        };

        if (this.onBeforeStepChanged) {
            const resp = this.onBeforeStepChanged(this._currentStep, value);
            if (resp instanceof Promise) {
                resp.then(callback).catch(e => {
                    if (e)
                        console.error(e);
                });
            }
            else {
                callback();
            }
        } else {
            callback();
        }
    }

    public constructor(protected bindingSignaler: BindingSignaler,
        private router: Router,
        private eventAggregator: EventAggregator) { }

    attached() {
        this._subscriptions.forEach(sub => sub.dispose());
        this._subscriptions = [];

        this._subscriptions.push(this.eventAggregator.subscribe(Messages.changeEntityItemStep, this.changeEntityItemStep));
    }

    changeEntityItemStep(model: { step: number })  {
        this.currentStep = this.steps.filter(x => x.step == model.step)[0];
    }

    detached() {
        this._subscriptions.forEach(sub => sub.dispose());
        this._subscriptions = [];
    }

    itemChanged() {
        if (this.currentStep) {
            this.bindingSignaler.signal('refresh-item-' + this.currentStep.step);
        }
    }

    getItemModel(step: StepModel): any {
        if (typeof (this.item) == "function") {
            return this.item(step);
        }
        if (step.getModel) {
            return step.getModel(this.item);
        }
        return this.item;
    }

    getShow(step: StepModel): boolean {
        let show = typeof (step.show) == "function" ? step.show() : step.show;

        if (step.lazyLoad) {
            show = show && step && this.currentStep && step.vm && step.vm == this.currentStep.vm;
        }

        return show;
    }

    bind() {
        if (!this.currentStep) {
            this.currentStep = this.defaultStepResolver ? this.defaultStepResolver(this.steps) : undefined;
        }
        if (!this.cacheTabKey) {
            this.cacheTabKey = "tab-" + this.router.currentInstruction.config.name;
        }
    }
}
